/**
 * Importar pacientes desde otro sistema: se elige un Excel (.xlsx) o CSV, Donna reconoce las columnas,
 * muestra qué filas están listas y cuáles tienen problemas, comprueba duplicados con la base y recién
 * entonces importa (por lotes). Nada se guarda hasta tocar «Importar».
 */
import { useMemo, useState } from "react";
import { api } from "../api.ts";
import { CAMPOS_IMPORTACION, NOMBRE_CAMPO_IMPORTACION, revisarHoja, type CampoImportacion, type PacienteImportado } from "../../../dominio/src/index.ts";
import { leerHoja } from "../xlsx.ts";
import { AvisoError, hoy, Seccion, useAccion } from "./comun.tsx";

const LOTE = 300;
interface Respuesta { prueba: boolean; creados: number; filas: { fila: number; hc: number }[]; omitidos: { fila: number; nombre: string; motivos: string[] }[] }

const PLANTILLA = "﻿Apellidos;Nombres;Cédula;Fecha de nacimiento;Sexo;Celular;Correo;Dirección;Ciudad;Ocupación;N.º de historia anterior;Representante;Cédula del representante;Celular del representante;Observaciones\r\n" +
  "Paredes Gómez;Lucía María;1710034065;15/03/1990;F;0991234567;lucia@correo.ec;Av. Amazonas y Colón;Quito;Contadora;1024;;;;\r\n";

function bajar(nombre: string, contenido: string) {
  const url = URL.createObjectURL(new Blob([contenido], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a"); a.href = url; a.download = nombre; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
const csv = (filas: (string | number)[][]) => "﻿" + filas.map((f) => f.map((c) => { const t = String(c ?? ""); return /[;"\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t; }).join(";")).join("\r\n");

export function ImportarPacientes() {
  const [archivo, setArchivo] = useState("");
  const [hoja, setHoja] = useState<string[][] | null>(null);
  const [mapa, setMapa] = useState<Partial<Record<CampoImportacion, number>> | null>(null);
  const [orden, setOrden] = useState<"apellidos" | "nombres">("apellidos");
  const [menores, setMenores] = useState(false);
  const [comprobado, setComprobado] = useState<Respuesta | null>(null);
  const [resultado, setResultado] = useState<{ creados: number; omitidos: Respuesta["omitidos"] } | null>(null);
  const [avance, setAvance] = useState<string>("");
  const a = useAccion();

  const revision = useMemo(() => (hoja ? revisarHoja(hoja, { orden, hoy: hoy(), mapa: mapa ?? undefined, menoresSinRepresentante: menores }) : null), [hoja, mapa, orden, menores]);
  const listas = revision?.filas.filter((f) => !f.errores.length) ?? [];
  const conError = revision?.filas.filter((f) => f.errores.length) ?? [];
  const conAviso = listas.filter((f) => f.avisos.length);

  async function elegir(f: File | undefined) {
    if (!f) return;
    setResultado(null); setComprobado(null); setHoja(null); setMapa(null);
    const filas = await a.correr(() => leerHoja(f));
    if (!filas) return;
    if (filas.length < 2) return a.setError(new Error("El archivo no tiene filas de pacientes debajo de los encabezados."));
    if (filas.length > 20001) return a.setError(new Error("El archivo tiene más de 20 000 filas: divídelo en partes."));
    setArchivo(f.name); setHoja(filas);
    setMapa(revisarHoja(filas.slice(0, 1)).mapa);
  }

  async function enviar(prueba: boolean, pacientes: PacienteImportado[]): Promise<Respuesta | undefined> {
    const total: Respuesta = { prueba, creados: 0, filas: [], omitidos: [] };
    for (let i = 0; i < pacientes.length; i += LOTE) {
      setAvance(`${prueba ? "Comprobando" : "Importando"} ${Math.min(i + LOTE, pacientes.length)} de ${pacientes.length}…`);
      const r = await api<Respuesta>("POST", "/api/pacientes/importar", { prueba, pacientes: pacientes.slice(i, i + LOTE) });
      total.creados += r.creados; total.filas.push(...r.filas); total.omitidos.push(...r.omitidos);
    }
    setAvance("");
    return total;
  }
  const comprobar = async () => { const r = await a.correr(() => enviar(true, listas.map((f) => f.paciente))); if (r) setComprobado(r); };
  async function importar() {
    if (!comprobado || !confirm(`¿Importar ${comprobado.creados} pacientes a Donna? Se les asignará un número de historia nuevo.`)) return;
    const r = await a.correr(() => enviar(false, listas.map((f) => f.paciente)));
    if (r) { setResultado({ creados: r.creados, omitidos: r.omitidos }); setComprobado(null); }
  }
  function bajarProblemas(omitidosServidor: Respuesta["omitidos"]) {
    if (!hoja || !revision) return;
    const motivo = new Map<number, string>();
    for (const f of conError) motivo.set(f.paciente.fila, f.errores.join(" "));
    for (const o of omitidosServidor) motivo.set(o.fila, o.motivos.join(" "));
    const filas = [...motivo.keys()].sort((x, y) => x - y).map((n) => [...(hoja[n - 1] ?? []), motivo.get(n)!]);
    bajar(`pacientes-sin-importar-${hoy()}.csv`, csv([[...hoja[0], "Motivo"], ...filas]));
  }

  const cambiarMapa = (campo: CampoImportacion, col: string) => {
    const m = { ...(mapa ?? {}) };
    for (const k of Object.keys(m) as CampoImportacion[]) if (m[k] === Number(col)) delete m[k];
    if (col === "") delete m[campo]; else m[campo] = Number(col);
    setMapa(m); setComprobado(null);
  };

  return (
    <div className="importar">
      <Seccion titulo="Importar pacientes">
        <p className="muted" style={{ margin: 0 }}>
          Trae los pacientes de tu sistema anterior desde un Excel (.xlsx) o un archivo de texto separado por comas, punto y coma o tabulaciones (.csv, .tab, .tsv, .txt). La primera fila debe tener los encabezados
          (Apellidos, Nombres, Cédula, Fecha de nacimiento, Celular…). Donna corrige los ceros que Excel quita a cédulas y celulares,
          no repite pacientes que ya existen y no guarda nada hasta que confirmes.
        </p>
        <div className="fila">
          <label className="btn pri" style={{ cursor: "pointer" }}>Elegir archivo
            <input type="file" accept=".xlsx,.csv,.tab,.tsv,.txt,text/csv,text/tab-separated-values,text/plain,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" hidden aria-label="Archivo de pacientes"
              onChange={(e) => { elegir(e.target.files?.[0]); e.target.value = ""; }} />
          </label>
          <button type="button" className="btn" onClick={() => bajar("plantilla-pacientes-donna.csv", PLANTILLA)}>Descargar plantilla</button>
          {archivo && <span className="muted">{archivo}</span>}
        </div>
        {avance && <div className="aviso info" role="status">{avance}</div>}
        <AvisoError error={a.error} />
      </Seccion>

      {resultado && (
        <Seccion titulo="Importación terminada">
          <div className="aviso ok" role="status">Se importaron {resultado.creados} paciente{resultado.creados === 1 ? "" : "s"}. Ya aparecen en la búsqueda.</div>
          {(resultado.omitidos.length > 0 || conError.length > 0) && (
            <p style={{ margin: 0 }}>{resultado.omitidos.length + conError.length} fila{resultado.omitidos.length + conError.length === 1 ? "" : "s"} no se importaron.{" "}
              <button className="btn chico" onClick={() => bajarProblemas(resultado.omitidos)}>Descargar las filas no importadas</button>
              <small className="muted"> Corrígelas en Excel y vuelve a importar ese archivo.</small></p>
          )}
        </Seccion>
      )}

      {revision && !resultado && (
        <>
          <Seccion titulo="1. Columnas reconocidas">
            {revision.faltan.map((f) => <div key={f} className="aviso error">{f}</div>)}
            <div className="rejilla" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(210px,1fr))" }}>
              {CAMPOS_IMPORTACION.map((c) => (
                <label key={c} className="campo">{NOMBRE_CAMPO_IMPORTACION[c]}
                  <select value={mapa?.[c] ?? ""} onChange={(e) => cambiarMapa(c, e.target.value)}>
                    <option value="">— No importar —</option>
                    {revision.encabezados.map((h, i) => <option key={i} value={i}>{h || `Columna ${i + 1}`}</option>)}
                  </select>
                </label>
              ))}
            </div>
            {mapa?.nombre_completo !== undefined && mapa.apellidos === undefined && (
              <label className="campo" style={{ maxWidth: 360 }}>El nombre completo empieza por
                <select value={orden} onChange={(e) => setOrden(e.target.value as "apellidos" | "nombres")}><option value="apellidos">Apellidos (Paredes Gómez Lucía)</option><option value="nombres">Nombres (Lucía Paredes Gómez)</option></select>
              </label>
            )}
          </Seccion>

          <Seccion titulo="2. Revisión">
            <div className="tiles">
              <div className="tile"><span>Filas en el archivo</span><b>{revision.filas.length}</b></div>
              <div className="tile"><span>Listas para importar</span><b>{listas.length}</b><small>{conAviso.length} con avisos</small></div>
              <div className="tile"><span>Con errores</span><b>{conError.length}</b><small>no se importan</small></div>
              <div className="tile"><span>Ya existen en Donna</span><b>{comprobado ? comprobado.omitidos.length : "—"}</b><small>{comprobado ? "se omiten" : "sin comprobar"}</small></div>
            </div>
            <label className="firmar" style={{ display: "block", margin: "8px 0" }}>
              <input type="checkbox" checked={menores} onChange={(e) => setMenores(e.target.checked)} />{" "}
              Importar también a los menores de edad sin representante legal (queda marcado en su ficha para agregarlo después)
            </label>
            {listas.length > 0 && (
              <div style={{ overflowX: "auto" }}>
                <table className="tabla-rep" aria-label="Vista previa">
                  <thead><tr><th>Fila</th><th>Paciente</th><th>Identificación</th><th>Nacimiento</th><th>Celular</th><th>Avisos</th></tr></thead>
                  <tbody>{listas.slice(0, 8).map((f) => (
                    <tr key={f.paciente.fila}><td className="mono">{f.paciente.fila}</td><td>{f.paciente.apellidos} {f.paciente.nombres}</td><td className="mono">{f.paciente.doc}</td>
                      <td className="mono">{f.paciente.nacimiento ?? ""}</td><td className="mono">{f.paciente.telefono}</td><td className="muted" style={{ fontSize: 12 }}>{f.avisos.join(" ")}</td></tr>
                  ))}</tbody>
                </table>
                {listas.length > 8 && <small className="muted">… y {listas.length - 8} más.</small>}
              </div>
            )}
            {conError.length > 0 && (
              <details className="problemas-imp" open={conError.length <= 10}>
                <summary>Filas con errores ({conError.length})</summary>
                <ul>{conError.slice(0, 100).map((f) => <li key={f.paciente.fila}><b>Fila {f.paciente.fila}</b>{f.paciente.nombres ? ` (${f.paciente.apellidos} ${f.paciente.nombres})` : ""}: {f.errores.join(" ")}</li>)}</ul>
                <button className="btn chico" onClick={() => bajarProblemas(comprobado?.omitidos ?? [])}>Descargar las filas con problemas</button>
              </details>
            )}
            {comprobado && comprobado.omitidos.length > 0 && (
              <details className="problemas-imp">
                <summary>Ya existen en Donna ({comprobado.omitidos.length})</summary>
                <ul>{comprobado.omitidos.slice(0, 100).map((o) => <li key={o.fila}><b>Fila {o.fila}</b> ({o.nombre}): {o.motivos.join(" ")}</li>)}</ul>
              </details>
            )}
            <div className="fila">
              {!comprobado
                ? <button className="btn pri" disabled={!listas.length || a.ocupado || revision.faltan.length > 0} onClick={comprobar}>Comprobar con los pacientes de Donna</button>
                : <button className="btn pri" disabled={!comprobado.creados || a.ocupado} onClick={importar}>Importar {comprobado.creados} paciente{comprobado.creados === 1 ? "" : "s"}</button>}
            </div>
          </Seccion>
        </>
      )}
    </div>
  );
}
